import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { NvFilePicker } from 'shared/components/nv-filepicker';
import NvIcon from 'shared/components/nv-icon';
import useUploadFile from 'shared/hooks/use-upload-file';
import { S3NameSpaces } from 'shared/services/s3-upload-factory';
import NvFileUploadProgress from 'shared/components/nv-file-upload-progress';
import NvUploadedFile from 'shared/components/nv-uploaded-file';
import { config } from '@config/config.json';


const MAX_FILE_SIZE = 750 * 1024 * 1024;

type VideoSectionProps = {
  setIsVideoUploading: (isUploading: boolean) => void;
};

const VideoSection = ({ setIsVideoUploading }: VideoSectionProps) => {
  // TODO: finish styling as needed
  const styles = css``;

  const dispatch = useAppDispatch();
  const { watch, setValue } = useFormContext();
  const video = watch('video');

  const {
    uploadFiles,
    isUploading,
    hasUploadPercentage,
    filesUploading,
    abortUpload,
  } = useUploadFile();
  const fileUploading = filesUploading[0];

  const onUploadFile = async (file) => {
    // Per Backend request, this is uploading to the ATTACHMENTS namespace
    setIsVideoUploading(true);
    const [novoedFile] = await uploadFiles([file], S3NameSpaces.ATTACHMENTS);

    // This handles when the user cancels the upload
    if (!novoedFile?.uniqueId) {
      setIsVideoUploading(false);
      return;
    }

    const formattedVideo = {
      uniqueId: novoedFile.uniqueId,
      fileName: novoedFile.name,
      fileSize: novoedFile.size,
      fileType: novoedFile.type,
      url: novoedFile.url,
    };
    setValue('video', formattedVideo, { shouldValidate: true });
    setIsVideoUploading(false);
  };

  const onDelete = () => {
    setValue('video', null, { shouldValidate: true });
  };

  const onFileError = () => {
    setIsVideoUploading(false);
    dispatch(addAlertMessage({
      type: AlertMessageType.ERROR,
      header: t.FORM.OOPS(),
      message: t.FILE_UPLOAD.NOT_SUPPORTED_ALERT(),
    }));
  };

  let content = (
    <NvFilePicker
      multiple={false}
      maxSize={MAX_FILE_SIZE}
      accept={config.files.videos.split(',')}
      onChange={([file]) => onUploadFile(file)}
      onSelectError={onFileError}
    >
      <div className='nv-file-upload d-flex justify-content-center align-items-center mt-2'>
        <NvIcon icon='upload' size='small' className='mr-2' />
        <div className='bold color-gray-2'>
          {t.FILE_UPLOAD.UPLOAD_VIDEO()}
        </div>
      </div>
    </NvFilePicker>
  );

  if (video?.fileName) {
    content = (
      <NvUploadedFile
        className='mt-2'
        file={{
          name: video.fileName,
          size: video.fileSize,
          type: video.fileType,
          url: video.url,
          uniqueId: video.uniqueId,
        }}
        onDelete={onDelete}
      />
    );
  }

  if (isUploading) {
    content = (
      <NvFileUploadProgress
        className='mt-2'
        showFileName={false}
        hasUploadPercentage={hasUploadPercentage}
        uploadInProgress={isUploading}
        fileUploading={fileUploading}
        abortUpload={abortUpload}
      />
    );
  }

  return (
    <div css={styles}>
      <div className='mt-4 semi-bold color-gray-2'>{t.MENTORING_PROGRAMS.GUIDES.CREATE_GUIDE_FLYOUT.VIDEO()}</div>
      <div className='mt-1'>{t.MENTORING_PROGRAMS.GUIDES.CREATE_GUIDE_FLYOUT.UPLOAD_ONE_VIDEO()}</div>
      {content}
    </div>
  );
};

export default VideoSection;
